import { Color } from '@persona-id/design-tokens';
import { CSSProperties, FC, MouseEvent, useRef, useState } from 'react';
import styled from 'styled-components';

import { isHovered } from 'design-system/lib/pseudoClass';
import useToggle from 'lib/hooks/useToggle';

const DIVIDER_HEIGHT = '4px';
const DIVIDER_WIDTH = '4px';

const DraggableDividerWrapper = styled.div<{ orientation: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: ${({ orientation }) => (orientation === 'horizontal' ? 'column' : 'row')};
`;

const Divider = styled.div<{ active: boolean; orientation: string }>`
  background-color: ${({ active }) => (active ? Color.CorePurple600 : Color.CorePurple200)};
  width: ${({ orientation }) => (orientation === 'horizontal' ? '100%' : DIVIDER_WIDTH)};
  height: ${({ orientation }) => (orientation === 'vertical' ? '100%' : DIVIDER_HEIGHT)};
  cursor: ${({ orientation }) => (orientation === 'horizontal' ? 'row-resize' : 'col-resize')};

  ${isHovered} {
    background-color: ${({ active }) => (active ? Color.CorePurple600 : Color.CorePurple500)};
  }
`;

interface DraggableDividerProps {
  orientation: 'horizontal' | 'vertical';
  firstComponent: JSX.Element;
  secondComponent: JSX.Element;
  firstMinHeight?: string;
  secondMinHeight?: string;
  firstMinWidth?: string;
  secondMinWidth?: string;
  firstStyle?: CSSProperties;
  secondStyle?: CSSProperties;
}

const DraggableDivider: FC<DraggableDividerProps> = ({
  orientation,
  firstComponent,
  secondComponent,
  firstMinHeight = '0px',
  firstMinWidth = '0px',
  secondMinHeight = '0px',
  secondMinWidth = '0px',
  firstStyle = {},
  secondStyle = {},
}: DraggableDividerProps) => {
  const [size, setSize] = useState('50%');
  const [dragging, { enable: startDragging, disable: endDragging }] = useToggle(false);
  const dragRef = useRef<HTMLDivElement>(null);

  const drag = (e: MouseEvent<HTMLDivElement>) => {
    const newSize =
      orientation === 'horizontal'
        ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          `${e.clientY - dragRef.current.getBoundingClientRect().top}px`
        : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          `${e.clientX - dragRef.current.getBoundingClientRect().left}px`;
    setSize(newSize);
  };

  return (
    <DraggableDividerWrapper
      ref={dragRef}
      orientation={orientation}
      onMouseMove={dragging ? drag : undefined}
      onMouseLeave={endDragging}
      onMouseUp={endDragging}
    >
      <div
        style={
          orientation === 'horizontal'
            ? {
                ...firstStyle,
                height: `clamp(${firstMinHeight}, ${size}, calc(100% - ${DIVIDER_HEIGHT} - ${secondMinHeight})`,
                pointerEvents: dragging ? 'none' : 'auto',
              }
            : {
                ...firstStyle,
                width: `clamp(${firstMinWidth}, ${size}, calc(100% - ${DIVIDER_WIDTH} - ${secondMinWidth})`,
                pointerEvents: dragging ? 'none' : 'auto',
              }
        }
      >
        {firstComponent}
      </div>
      <Divider active={dragging} orientation={orientation} onMouseDown={startDragging} />
      <div
        style={
          orientation === 'horizontal'
            ? {
                ...secondStyle,
                height: `clamp(${secondMinHeight}, calc(100% - ${DIVIDER_HEIGHT} - ${size}), calc(100% - ${DIVIDER_HEIGHT} - ${firstMinHeight})`,
                pointerEvents: dragging ? 'none' : 'auto',
              }
            : {
                ...secondStyle,
                width: `clamp(${secondMinWidth}, calc(100% - ${DIVIDER_WIDTH} - ${size}), calc(100% - ${DIVIDER_WIDTH} - ${firstMinWidth})`,
                pointerEvents: dragging ? 'none' : 'auto',
              }
        }
      >
        {secondComponent}
      </div>
    </DraggableDividerWrapper>
  );
};

export default DraggableDivider;
